import { Injectable } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { DateTime } from "@lib/date-time";
import { BehaviorSubject, OverrideSubject, combineLatest, interval, of, op } from "@lib/rxjs";
import { storageLocal } from "@lib/storage-local";
import { Device, User, VehicleLocationQuery, vantagePointMuckpileQuery } from "@models";
import { WebClient } from "./web-client";
import { WebsocketService } from "@app/new-ui/video-streaming/websocket.service";
import { FragQueryService } from "./frag-query.service";
import { WhereTable } from "@lib/where";
import { asFiniteNumber } from "@lib/number";

interface DurationOption {
	label: string;
	type: DateTime.Step;
	key?: string;
}

const durationOptions: DurationOption[] = [
	{
		label: 'Last 10 minutes',
		type: [10, 'minute'],
	},
	{
		label: 'Last 30 minutes',
		type: [30, 'minute'],
	},
	{
		label: 'Last hour',
		type: [1, 'hour'],
	},
	{
		label: 'Last 2 hour',
		type: [2, 'hour'],
	},
	{
		label: 'Last 3 Hours',
		type: [3, 'hour'],
	},
	{
		label: 'Last 6 Hours',
		type: [6, 'hour'],
	},
	{
		label: 'Last 12 Hours',
		type: [12, 'hour'],
	},
	{
		label: 'Last 24 Hours',
		type: [1, 'day'],
	}
];

for (const opt of durationOptions)
	opt.key = Array.isArray(opt.type) ? opt.type.join('') : opt.type;
const durationOptionMap = new Map(durationOptions.map((v) => [v.key, v]));

function liveTimeRange(step: DateTime.Step) {
	return interval(30 * 1000)
		.pipe(
			op.startWith(-1),
			op.map(() => {
				const end = new DateTime();
				const begin = end.clone().sub(step);
				return {
					begin,
					end,
				};
			}));
}

@Injectable({
	providedIn: "root",
})
export class VehicleQueryService {
	constructor(
		private readonly webClient: WebClient,
		private readonly user: User,
		private readonly webSocket: WebsocketService,
		public readonly fragQuery: FragQueryService,
		private readonly snackBar: MatSnackBar,
	) {
		this.init();
	}
    public readonly muckpiles$ = this.fragQuery.muckpiles$;
    public trigger$ = new BehaviorSubject<void>(null);
	public readonly vpMuckpileRefresh$ = new BehaviorSubject<void>(null);

	//==================== traceing page data variables ==============//
	public readonly defaultDevices$ = this.trigger$.pipe(
		op.switchMap(() => this.webClient.model.device.search$({
			sort: {
				clientId: '+',
				id: '+'
			}
		}))
	);

	public readonly defaultDeviceUuid$ = this.defaultDevices$.pipe(
        op.map((v) => v?.[0]?.uuid ?? null)
    );

	public readonly durationOptions = of(durationOptions);
	public readonly defaultDevice$ = this.defaultDevices$.pipe(op.map((v) => v?.[0] ?? null));
	public readonly siteId$ = this.fragQuery.siteId$;
	public readonly vehicles$ = this.siteId$.pipe(
		op.switchMap((siteId) => {
			const selectedSiteId = asFiniteNumber(siteId || this.user.siteId || 0);
			const search = selectedSiteId
				? { where: { siteId: selectedSiteId }, sort: { id: '+' as const } }
				: { sort: { id: '+' as const } };
			return this.webClient.model.vehicle.search$(search);
		}),
		op.shareReplay(1),
	);
	public readonly defaultSelectedVehicles$ = this.vehicles$;
	public liveVehicle$ = new BehaviorSubject([]);
	public readonly sites$ = this.webClient.model.blastSite.search$({});
	public readonly deviceUuid$: OverrideSubject<string> = new OverrideSubject(this.defaultDeviceUuid$);
	public readonly devices$ = combineLatest([
		this.defaultDevices$,
		this.siteId$
	]).pipe(
		op.map(([devices, siteId]) => devices.filter(d => d.siteId === siteId) ?? null)
	);

	public defaultDurationOption = this.durationOptions.pipe(
		op.map(options => {
			const savedKey = storageLocal.get('vehicle-query-duration');
			const savedOption = savedKey ? durationOptionMap.get(savedKey) : undefined;

			return savedOption && options.includes(savedOption)
				? savedOption.key
				: '1hour';
		})
	);

	public readonly duration$ = new OverrideSubject(this.defaultDurationOption);

	public readonly timeRange$ = this.duration$.pipe(
		op.map((key) => durationOptionMap.get(key) ?? durationOptionMap.get('1hour')),
		op.switchMap(durOpt => {
			const step = durOpt.type;
			return liveTimeRange(step);
		}));

	public readonly vehicleLocationForInterval$ = combineLatest(
		[
			this.timeRange$,
			this.defaultSelectedVehicles$,
		]
	).pipe(
		op.debounceTime(0),
		op.switchMap(([timeRange, vehicles]) => {
			const vehicleIds = vehicles.map(vehicle => vehicle.id).join(',');
			const body: VehicleLocationQuery.Interval.Body = {
				email: this.user.email,
				authToken: this.user.authToken,
				vehicleIds,
				begin: timeRange.begin,
				end: timeRange.end
			};

			return this.webClient
				.rest$({
					route: 'vehicle/location/interval',
					method: 'get',
					body,
				})
				.pipe(op.startWith(undefined))
		}),
		op.traverse((r) => r.toRecord<VehicleLocationQuery.Interval.Response>()),
		op.traverse((r) => ({
			begin: (this.user.timezone === null || this.user.timezone === 'Default') ? new DateTime(r.begin) : new DateTime(r.begin).replaceTimezone(this.user.timezoneOffset),
			end: (this.user.timezone === null || this.user.timezone === 'Default') ? new DateTime(r.end) : new DateTime(r.end).replaceTimezone(this.user.timezoneOffset),
			tracePaths: r.tracePaths
		})),
		op.shareReplay(1)
	)

	private init() {
		this.devices$.subscribe((devices) => {
			let device: Device = null;
			if (devices) {
				const uuid = '' + storageLocal.get('frag-query-device-uuid');
				device = devices.find((device) => device.uuid === uuid);
				if (!device) device = devices[0];
			}
			if (device?.uuid)
				this.deviceUuid$.next(device.uuid);
		});

		this.duration$.subscribe((v) => storageLocal.set('vehicle-query-duration', v));

		this.siteId$.subscribe((siteId) => {
			const selectedSiteId = asFiniteNumber(siteId || 0);
			this.webSocket.sendMessage({
				module: 'location',
				type: 'vehicle',
				siteId: selectedSiteId || null,
			});
		});

		this.webSocket.getMessages().subscribe(
			message => {
				if (message.module == 'location') {
					let updatedVehicleLocation = message.vehicles;

					this.liveVehicle$.next(updatedVehicleLocation);
				}

			},
			error => {
				console.error('WebSocket error:', error);
			}
		);
	}

	public readonly geofenceRecords$ = combineLatest([
		this.trigger$,
		this.timeRange$,
		this.muckpiles$,
		this.vehicles$,
	]).pipe(
		op.switchMap(([_, timeRange, muckpiles, vehicles]) => {
			const beginTime = Math.floor(timeRange.begin.valueOf() / 1000);
			const endTime = Math.floor(timeRange.end.valueOf() / 1000);
			const muckpileIds = muckpiles ? muckpiles?.map((m) => m.id) : []

			if (muckpileIds.length === 0) {
				return of([]);
			}
			const whereClause: WhereTable = {
				time: [['>', beginTime], ['<', endTime]],
			};

			if (muckpileIds.length > 0) {
				whereClause['muckpileId'] = [['in', muckpileIds]];
			}

			return this.webClient.model.geofenceRecord.search$({
				where: whereClause,
				sort: { time: '+' }
			}).pipe(
				op.map(records => records.map(record => ({
					...record,
					time: (this.user.timezone === null || this.user.timezone === 'Default') ? new DateTime(record.time * 1000) : new DateTime(record.time * 1000).replaceTimezone(this.user.timezoneOffset),
					muckpileName: muckpiles?.find(m => m.id === record.muckpileId)?.name || 'Unknown Muckpile',
					vehicleName: vehicles?.find(v => v.externalId === record.vehicleId)?.name || 'Unknown Vehicle'
				})))
			);
		}),
		op.tap((records) => console.log("Enriched Geofence Records:", records)),
		op.shareReplay(1)
	);

	private getEmptyVpMuckpileResponse(): vantagePointMuckpileQuery.list.Response {
		return {
			siteId: 0,
			configFound: false,
			siteGuid: null,
			existingMuckpiles: [],
			nonExistingMuckpiles: [],
		};
	}

	public readonly vpMuckpileManager$ = combineLatest([
		this.siteId$,
		this.vpMuckpileRefresh$
	]).pipe(
		op.switchMap(([siteId]) => {
			const selectedSiteId = asFiniteNumber(siteId);
			if (!selectedSiteId) {
				return of(null);
			}

			const body: vantagePointMuckpileQuery.list.Body = {
				email: this.user.email,
				authToken: this.user.authToken,
				siteId: selectedSiteId,
			};

			return this.webClient.rest$({
				route: 'vp-muckpile-query/list',
				method: 'get',
				body,
			}).pipe(op.startWith(undefined));
		}),
		op.map((response) => response === undefined
			? undefined
			: response === null
				? this.getEmptyVpMuckpileResponse()
				: response.toRecord<vantagePointMuckpileQuery.list.Response>()),
		op.shareReplay(1)
	);

	public refreshVpMuckpileManager() {
		this.vpMuckpileRefresh$.next(null);
	}

	public async handleVpMuckpileAction(
		regionGuids: string[],
		action: 'add' | 'sync'
	) {
		const selectedSiteId = asFiniteNumber(this.siteId$.value);

		const cleanedRegionGuids = (Array.isArray(regionGuids) ? regionGuids : [])
			.map((regionGuid) => `${regionGuid || ''}`.trim())
			.filter((regionGuid) => regionGuid.length > 0);

		if (!selectedSiteId || cleanedRegionGuids.length === 0) {
			return null;
		}

		try {
			const response = await this.webClient.rest({
				route: 'vp-muckpile-query/add',
				method: 'post',
				body: {
					email: this.user.email,
					authToken: this.user.authToken,
					siteId: selectedSiteId,
					regionGuids: cleanedRegionGuids,
				} as vantagePointMuckpileQuery.add.Body,
			});

			const result = response.toRecord<vantagePointMuckpileQuery.add.Response>();

			if (result.success) {
				this.snackBar.open(`${result.added} VP muckpile(s) ${action}ed successfully.`, 'OK',
					{
						duration: 4000,
						panelClass: ['snackbar-success'],
					}
				);

				this.refreshVpMuckpileManager();
				this.fragQuery.trigger$.next();
			} else {
				this.snackBar.open(`No VP muckpiles were ${action}ed.`, 'OK',
					{ duration: 4000 }
				);
			}

			return result;
		} catch (error) {
			console.error(`Error ${action}ing VP muckpiles:`, error);

			this.snackBar.open(`Failed to ${action} VP muckpiles. Please try again.`, 'OK',
				{
					duration: 5000,
					panelClass: ['snackbar-error'],
				}
			);

			return null;
		}
	}

	public async removeVpMuckpiles(regionGuids: string[]) {
		const selectedSiteId = asFiniteNumber(this.siteId$.value);
		const cleanedRegionGuids = (Array.isArray(regionGuids) ? regionGuids : [])
			.map((regionGuid) => `${regionGuid || ''}`.trim())
			.filter((regionGuid) => regionGuid.length > 0);

		if (!selectedSiteId || cleanedRegionGuids.length === 0) {
			return null;
		}

		try {
			const response = await this.webClient.rest({
				route: 'vp-muckpile-query/remove',
				method: 'post',
				body: {
					email: this.user.email,
					authToken: this.user.authToken,
					siteId: selectedSiteId,
					regionGuids: cleanedRegionGuids,
				} as vantagePointMuckpileQuery.remove.Body,
			});
			const result = response.toRecord<vantagePointMuckpileQuery.remove.Response>();

			if (result.success) {
				this.snackBar.open(`${result.removed} VP muckpile(s) removed successfully.`, 'OK', {
					duration: 4000,
					panelClass: ['snackbar-success'],
				});
				this.refreshVpMuckpileManager();
				this.fragQuery.trigger$.next();
			} else {
				this.snackBar.open('No VP muckpiles were removed.', 'OK', {
					duration: 4000,
				});
			}

			return result;
		} catch (error) {
			console.error('Error removing VP muckpiles:', error);
			this.snackBar.open('Failed to remove VP muckpiles. Please try again.', 'OK', {
				duration: 5000,
				panelClass: ['snackbar-error'],
			});
			return null;
		}
	}
}
